import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import _ from "lodash";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {ClientFamilyConcernInput} from "../../domain";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";

type Props = {
    visible$: Subject<boolean>;
    onFinished: (formValues: ClientFamilyConcernInput | null) => Promise<void>;
};

export function ClientFamilyConcernFormModal({visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<ClientFamilyConcernInput>({
        defaultValues: {
            familyConcern: "",
            attemptToResolve: "",
            familyResponse: "",
            requestedSupport: ""
        }
    });

    const {promiseInProgress} = usePromiseTracker();
    const isCallingBackend = promiseInProgress;

    const submitWithTracking = useCallback((formValues: ClientFamilyConcernInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Client/Family Concern is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);
    
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: ClientFamilyConcernInput) => {
            await submitWithTracking(_.pick(data, [
                'familyConcern',
                'attemptToResolve',
                'familyResponse',
                'requestedSupport'
            ]));
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);


    return (
        <ReportModal visible={visible} title={'Client/Family Concern'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!isCallingBackend && (<>
                        <div className="px-6 py-4">
                            <div className="space-y-6">
                                <ModalTextAreaInput
                                    disabled={isCallingBackend}
                                    field="familyConcern"
                                    label="What was the family concern?"
                                    placeholder="Enter the family concern here..."
                                    labelPosition="top"
                                    rowsNum={4}
                                    maxLength={4000}
                                    required
                                    requiredMessage="Please tell us the family concern."
                                    showCharacterCount
                                    shouldStopInputOnMaxLength={false}
                                />
                                <ModalTextAreaInput
                                    disabled={isCallingBackend}
                                    field="attemptToResolve"
                                    label="How did you attempt to resolve the concern?"
                                    placeholder="Describe how you attempted to resolve the concern..."
                                    labelPosition="top"
                                    rowsNum={4}
                                    maxLength={4000}
                                    required
                                    requiredMessage="Please tell us how you attempted to resolve the concern."
                                    showCharacterCount
                                    shouldStopInputOnMaxLength={false}
                                />
                                <ModalTextAreaInput
                                    disabled={isCallingBackend}
                                    field="familyResponse"
                                    label="What was the family/client response?"
                                    placeholder="Enter the family/client response..."
                                    labelPosition="top"
                                    rowsNum={4}
                                    maxLength={4000}
                                    required
                                    requiredMessage="Please tell us the family/client response."
                                    showCharacterCount
                                    shouldStopInputOnMaxLength={false}
                                />
                                <ModalTextAreaInput
                                    disabled={isCallingBackend}
                                    field="requestedSupport"
                                    label="How can we best support you at this time?"
                                    placeholder="Describe how we can support you..."
                                    labelPosition="top"
                                    rowsNum={4}
                                    maxLength={4000}
                                    requiredMessage="Please tell us how we can support you at this time."
                                    showCharacterCount
                                    required
                                    shouldStopInputOnMaxLength={false}
                                />
                            </div>
                        </div>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={"Cancel"}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                            </div>
                        </div>
                    </>)}

                    {isCallingBackend &&
                        <div className="p-24">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}
